import { MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import * as TypeStyle from 'typestyle';

import { Messages } from '~src/codegen/Localize';

import {
  ButtonControlledChildComponentProps,
  ButtonControlledComponent,
  ButtonProps
} from './ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu } from './ButtonControlledFlyoutListMenu';

export type GenericActionsMenuItem = {
  /* Info for displaying the action as menu item */
  menuItemInfo: MenuItemInfo;
  /* Callback for when the action is selected */
  execute: () => void;
};

export type GenericActionsMenuProps = {
  /* Actions to be displayed on the flyout */
  actions: ReadonlyArray<GenericActionsMenuItem>;
  /* Content to be displayed on the menu button; if not provided, by default a dropdown arrow would be displayed */
  children?: React.ReactChild;
};

/** 
 * Unlike the `ActionsMenu` which is tightly coupled with content actions being tracked in Redux store,
 * this `GenericActionsMenu` is simply a wrapper around `ButtonControlledComponent`
 * and takes care of building the props for the menu button and the flyout.
 */
export class GenericActionsMenu extends React.Component<GenericActionsMenuProps> {

  private readonly memoizedGetGenericActionsMenuButtonComponent = defaultMemoize(getGenericActionsMenuButtonComponent);
  private readonly memoizedGetGenericActionsMenuChildComponent = defaultMemoize(getGenericActionsMenuChildComponent);

  public render(): JSX.Element {
    const button = this.memoizedGetGenericActionsMenuButtonComponent(this.props.children);
    const child = this.memoizedGetGenericActionsMenuChildComponent(this.props.actions);
    return (
      <ButtonControlledComponent
        buttonComponent={button}
        childComponent={child}
        testId={'generic-action-menu-test-id'}
      />
    );
  }
}

const GenericActionsFlyoutId = 'generic-actions-flyout';
const GenericActionsMenuButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
  }
});

function getGenericActionsMenuButtonComponent(children?: React.ReactChild): React.ComponentType<ButtonProps> {
  return function actionsMenuButton(): JSX.Element {
    return <MenuButtonWidget
      aria-controls={GenericActionsFlyoutId}
      aria-label={Messages.moreActions()}
      children={children}
      className={GenericActionsMenuButtonStyleName}
      testId={'generic-action-menu-dropdown-button'}
    />;
  };
}

function getGenericActionsMenuChildComponent(actions: ReadonlyArray<GenericActionsMenuItem>): React.ComponentType<ButtonControlledChildComponentProps> {
  const menuItems: MenuItemInfo[] = actions.map((action, index) => ({
    ...action.menuItemInfo,
    value: `${index}`,
  }));
  const itemSelected = (value: string) => {
    const index = parseInt(value, 10);
    const action = actions[index];
    if (action) {
      action.execute();
    }
  };
  return function actionsMenuChild(props: ButtonControlledChildComponentProps): JSX.Element {
    return <ButtonControlledFlyoutListMenu
      anchorElement={props.anchorElement}
      closeMe={props.closeMe}
      id={GenericActionsFlyoutId}
      menuItems={menuItems}
      menuStyle={{ width: 300 }}
      onComponentDidMount={null}
      onMenuItemSelected={itemSelected}
      testId={'generic-action-menu-wrapper'}
    />;
  };
}
